import { useListPagination } from '@/hooks/useListPagination'
import { ListPagination } from '@/components/ui/list-pagination'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useSortingState } from '@/hooks/useSortingState'
import { SortableTableHead } from '@/components/ui/sortable-table-head'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  UserIcon,
  PencilSquareIcon,
  TrashIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline'
import { useDestinatari, useDeleteDestinatario } from '@/hooks/useOrdini'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

/**
 * Pagina lista destinatari.
 * 
 * FASE 7.4: Spedizioni Avanzate
 * 
 * Features:
 * - Tabella destinatari con ricerca
 * - Filtri per tipo (privato/azienda)
 * - Azioni rapide (modifica, elimina)
 * - Badge per tipo destinatario
 */
export default function DestinatariList() {
  const [search, setSearch] = useState('')
  const [ordering, setOrdering] = useSortingState('destinatari', 'nome_completo')
  const [deleteId, setDeleteId] = useState(null)

  // Query destinatari
  const { page, setPage } = useListPagination({ search, ordering })

  const { data: destinatariData, isLoading, isFetching, error } = useDestinatari({ page, search, ordering })
  const deleteMutation = useDeleteDestinatario()

  // Handler eliminazione
  const handleDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId, {
        onSuccess: () => setDeleteId(null)
      })
    }
  }

  // DRF può restituire array diretto o oggetto paginato con results
  const destinatari = Array.isArray(destinatariData) 
    ? destinatariData 
    : (destinatariData?.results || [])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Destinatari</h1>
          <p className="text-muted-foreground">
            Gestisci l&apos;anagrafica dei destinatari per le spedizioni
          </p>
        </div>
        <Button asChild>
          <Link to="/ordini/destinatari/nuovo">
            <PlusIcon className="mr-2 h-4 w-4" />
            Nuovo Destinatario
          </Link>
        </Button>
      </div>

      {/* Filtri */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">Filtri</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Cerca per nome, azienda, telefono o indirizzo..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabella */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead field="nome_completo" currentOrdering={ordering} onSort={setOrdering}>
                  Nome Completo
                </SortableTableHead>
                <SortableTableHead field="azienda" currentOrdering={ordering} onSort={setOrdering}>Tipo</SortableTableHead>
                <SortableTableHead field="telefono" currentOrdering={ordering} onSort={setOrdering}>Telefono</SortableTableHead>
                <SortableTableHead field="email" currentOrdering={ordering} onSort={setOrdering}>Email</SortableTableHead>
                <TableHead>Indirizzo predefinito</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    Caricamento...
                  </TableCell>
                </TableRow>
              ) : destinatari.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2">
                      <UserIcon className="h-12 w-12 text-muted-foreground" />
                      <p className="text-muted-foreground">
                        {search ? 'Nessun destinatario trovato' : 'Nessun destinatario presente'}
                      </p>
                      {!search && (
                        <Button asChild variant="outline" size="sm">
                          <Link to="/ordini/destinatari/nuovo">
                            <PlusIcon className="mr-2 h-4 w-4" />
                            Crea il primo destinatario
                          </Link>
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                destinatari.map((destinatario) => (
                  <TableRow key={destinatario.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {destinatario.azienda ? (
                          <BuildingOfficeIcon className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <UserIcon className="h-4 w-4 text-muted-foreground" />
                        )}
                        <div>
                          <div className="font-medium">{destinatario.nome_completo}</div>
                          {destinatario.azienda && (
                            <div className="text-sm text-muted-foreground">
                              {destinatario.nome} {destinatario.cognome}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={destinatario.azienda ? 'default' : 'secondary'}>
                        {destinatario.azienda ? 'Azienda' : 'Privato'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm">
                        <PhoneIcon className="h-3 w-3 text-muted-foreground" />
                        {destinatario.telefono}
                      </div>
                    </TableCell>
                    <TableCell>
                      {destinatario.email ? (
                        <div className="flex items-center gap-2 text-sm">
                          <EnvelopeIcon className="h-3 w-3 text-muted-foreground" />
                          {destinatario.email}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const indirizzo = destinatario.indirizzi?.find((item) => item.predefinito)
                          || destinatario.indirizzi?.[0]
                        return indirizzo ? (
                          <div className="text-sm">
                            <div>{indirizzo.via} {indirizzo.civico}</div>
                            <div className="text-muted-foreground">
                              {indirizzo.cap} {indirizzo.citta}{indirizzo.provincia ? ` (${indirizzo.provincia})` : ''}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-amber-700">Da inserire</span>
                        )
                      })()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button asChild variant="ghost" size="sm">
                          <Link to={`/ordini/destinatari/${destinatario.id}/modifica`}>
                            <PencilSquareIcon className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteId(destinatario.id)}
                        >
                          <TrashIcon className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Statistiche */}
      {destinatari.length > 0 && (
        <div className="text-sm text-muted-foreground">
          Totale destinatari: <strong>{destinatari.length}</strong>
        </div>
      )}

      {/* Dialog conferma eliminazione */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare questo destinatario? 
              {' '}Questa azione non può essere annullata.
              {' '}Se il destinatario è associato a colli esistenti, l&apos;eliminazione potrebbe fallire.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ListPagination data={destinatariData} page={page} setPage={setPage} isFetching={isFetching} error={error} />
    </div>
  )
}
